import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import type { AgentMessage } from '@/context/AgentChatContext';
import type { AgentProgressStep } from '@/context/AgentChatContext';

interface AgentMessageBubbleProps {
  message: AgentMessage;
}

export function AgentMessageBubble({ message }: AgentMessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  // Render a resolved card summary (read-only)
  if (message.resolvedCard) {
    return <ResolvedCardSummary card={message.resolvedCard} />;
  }

  if (!message.content && !message.toolCall) return null;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-primary-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        } ${message.isStreaming ? 'animate-pulse' : ''}`}
      >
        {message.toolCall ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 italic">
            <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            {t('agentChat.callingTool', { tool: message.toolCall.tool })}
          </div>
        ) : isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content || ''}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

/** Read-only summary of a card that was shown and responded to. */
function ResolvedCardSummary({
  card,
}: {
  card: NonNullable<AgentMessage['resolvedCard']>;
}) {
  const { t } = useTranslation();
  const { cardType, data, selection } = card;

  const label = (() => {
    switch (cardType) {
      case 'customer_selector': {
        const name = (selection.customer_name as string) || '';
        return name
          ? t('agentChat.resolved.customerSelected', { name })
          : t('agentChat.resolved.customerDefault');
      }
      case 'product_selector': {
        const items = selection.items as Array<{ name: string; quantity: number }> | undefined;
        if (items && items.length > 0) {
          return t('agentChat.resolved.productsSelected', { count: items.length });
        }
        return t('agentChat.resolved.productsDefault');
      }
      case 'confirmation':
        return selection.confirmed
          ? t('agentChat.resolved.confirmed')
          : t('agentChat.resolved.cancelled');
      case 'question_card': {
        const answer =
          (selection.answer as string) ||
          (selection.custom_answer as string) ||
          (selection.title as string) ||
          '';
        return answer
          ? t('agentChat.resolved.questionAnswered', { answer })
          : t('agentChat.resolved.selectionMade');
      }
      case 'document_builder':
        return selection.action === 'created'
          ? t('agentChat.resolved.documentCreated', {
              type: (selection.document_type_name as string) || t('createDocument.title', 'Create Document'),
              number: selection.document_number || '—',
            })
          : t('agentChat.resolved.documentCancelled');
      case 'order_summary':
        return t('agentChat.resolved.order', {
          customer: (data as Record<string, unknown>).customer_name || '',
        });
      case 'capability_editor': {
        const customerName = (selection.customer_name as string) || '';
        const items = selection.items as Array<{ name?: string }> | undefined;
        const main = customerName
          ? t('agentChat.resolved.draftUpdatedFor', { customer: customerName })
          : t('agentChat.resolved.draftUpdated');
        const count = items?.length ? t('agentChat.resolved.itemsCount', { count: items.length }) : '';
        return `${main}${count}`;
      }
      case 'insight_kpi':
        return t('agentChat.resolved.kpiCard', {
          title: (data as Record<string, unknown>).title || 'Insight',
        });
      case 'insight_chart':
        return t('agentChat.resolved.chartCard', {
          title: (data as Record<string, unknown>).title || 'Trend',
        });
      case 'create_document_cta':
        return selection.action === 'open_create_document'
          ? t('agentChat.resolved.openedDraft')
          : t('agentChat.resolved.draftDismissed');
      default:
        return t('agentChat.resolved.selectionMade');
    }
  })();

  const details = (() => {
    if (cardType === 'question_card') {
      const question = (data as Record<string, unknown>).question as string | undefined;
      return question ? [question] : null;
    }
    if (cardType === 'document_builder' && selection.action === 'created') {
      const details: string[] = [];
      if (selection.customer_name) details.push(String(selection.customer_name));
      if (selection.total != null) details.push(`₪${Number(selection.total).toFixed(2)}`);
      return details;
    }
    if (cardType === 'product_selector') {
      const items = selection.items as Array<{ name: string; quantity: number; unit?: string }> | undefined;
      if (items && items.length > 0) {
        return items.map((i) => `${i.name} ×${i.quantity}${i.unit ? ` ${i.unit}` : ''}`);
      }
    }
    return null;
  })();

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[85%] rounded-2xl bg-primary-50 border border-primary-200 px-4 py-2.5 text-sm">
        <span className="font-medium text-primary-800">{label}</span>
        {details && (
          <ul className="mt-1 text-xs text-primary-600 space-y-0.5">
            {details.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        {cardType === 'document_builder' && selection.action === 'created' && Boolean(selection.document_link) && (
          <button
            type="button"
            onClick={() => window.open(String(selection.document_link), '_blank', 'noopener,noreferrer')}
            className="mt-2 inline-flex rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-primary-700 shadow-sm ring-1 ring-primary-200 transition hover:bg-primary-100"
          >
            {t('createDocument.success.openPdf', 'Open PDF')}
          </button>
        )}
      </div>
    </div>
  );
}

interface ToolCallIndicatorProps {
  toolName: string;
}

export function ToolCallIndicator({ toolName }: ToolCallIndicatorProps) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-start mb-3">
      <div className="flex items-center gap-2 rounded-2xl bg-gray-50 px-4 py-2 text-xs text-gray-500 border border-gray-200">
        <span className="inline-block w-3 h-3 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
        <span>{t('agentChat.callingTool', { tool: toolName })}</span>
      </div>
    </div>
  );
}

interface AgentProgressPanelProps {
  steps: AgentProgressStep[];
}

export function AgentProgressPanel({ steps }: AgentProgressPanelProps) {
  const { t } = useTranslation();

  if (steps.length === 0) return null;

  return (
    <div className="mb-4 flex justify-start">
      <div className="w-full max-w-[85%] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-500">
          <span className="inline-block h-2 w-2 rounded-full bg-primary-500" />
          <span>{t('agentChat.progress.title', 'What the assistant is doing')}</span>
        </div>
        <div className="space-y-2">
          {steps.map((step) => {
            const isActive = step.status === 'in_progress';
            const isWaiting = step.status === 'waiting';
            const isCompleted = step.status === 'completed';

            return (
              <div
                key={step.key}
                className={`flex items-center gap-3 rounded-xl px-2 py-1.5 text-sm ${
                  isActive || isWaiting ? 'bg-slate-50' : ''
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
                    isCompleted
                      ? 'bg-emerald-100 text-emerald-700'
                      : isWaiting
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-primary-100 text-primary-700'
                  }`}
                >
                  {isCompleted ? '✓' : isWaiting ? '…' : ''}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-slate-800">{step.label}</div>
                  <div className="text-xs text-slate-500">
                    {isCompleted
                      ? t('agentChat.progress.completed', 'Done')
                      : isWaiting
                        ? t('agentChat.progress.waiting', 'Waiting for your input')
                        : t('agentChat.progress.inProgress', 'In progress')}
                  </div>
                </div>
                {isActive && (
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
